import * as signalR from "@microsoft/signalr";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { getOrCreateManager } from "./SignalRManagerRegistry";

export class SignalRConnectionManager {
    private connection: HubConnection;
    private starting = false;
    private startQueue: { resolve: () => void; reject: (err: unknown) => void }[] = [];

    constructor(connection: HubConnection) {
        this.connection = connection;
    }

    /** Wait until the connection leaves a transient state (Connecting/Disconnecting). */
    private waitForStableState(): Promise<void> {
        return new Promise<void>((resolve) => {
            const check = () => {
                const s = this.connection.state;
                if (s === HubConnectionState.Connected || s === HubConnectionState.Disconnected) {
                    resolve();
                } else {
                    // Poll briefly — SignalR JS client doesn't expose a
                    // "state-changed" event, so a short poll is the safest option.
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }

    public async start(): Promise<void> {
        if (this.connection.state === HubConnectionState.Connected) {
            return;
        }

        if (this.starting) {
            return new Promise((resolve, reject) => this.startQueue.push({ resolve, reject }));
        }

        this.starting = true;
        let startError: unknown = null;

        try {
            // If the connection is in a transient state (Connecting / Disconnecting)
            // wait for it to settle before attempting to start.
            if (
                this.connection.state === HubConnectionState.Connecting ||
                this.connection.state === HubConnectionState.Disconnecting
            ) {
                await this.waitForStableState();
            }

            // After waiting, re-read the state. TypeScript's narrowing doesn't
            // account for the async gap, so compare against the string value.
            const settled = this.connection.state as string;
            if (settled === HubConnectionState.Connected) {
                return;
            }

            if (settled === HubConnectionState.Disconnected) {
                await this.connection.start();
                console.log("[SignalRConnectionManager] Connection started.");
            }
        } catch (err) {
            console.error("[SignalRConnectionManager] Error during start:", err);
            startError = err;
            throw err;
        } finally {
            this.starting = false;
            this.startQueue.forEach(({ resolve, reject }) => {
                if (startError) {
                    reject(startError);
                } else {
                    resolve();
                }
            });
            this.startQueue = [];
        }
    }

    public async stop(): Promise<void> {
        if (this.connection.state === HubConnectionState.Connected) {
            await this.connection.stop();
            console.log("[SignalRConnectionManager] Connection stopped.");
        }
    }
}

export const stopConnection = async (connection: signalR.HubConnection) => {
    if (
        connection.state === HubConnectionState.Disconnected ||
        connection.state === HubConnectionState.Disconnecting
    ) {
        return;
    }
    try {
        await connection.stop();
        console.log("SignalR stopped");
    } catch (err) {
        console.error("Error stopping SignalR connection:", err);
    }
};

export const restartConnection = async (connection: signalR.HubConnection) => {
    const manager = getOrCreateManager(connection);
    try {
        await manager.stop();
        await manager.start();
        console.log("[restartConnection] Connection restarted successfully.");
    } catch (err) {
        console.error("[restartConnection] Error restarting connection:", err);
    }
};